import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import FieldShell from '../shared/TailwindComponents/FieldShell';
import { ColumnDataType } from '../../types/batchTables';

/**
 * The three fields that describe a table column — name, AI query, data type.
 *
 * They existed three times over (`AddColumnModal`, `AddTableColumnContent`,
 * `EditColumnContent`) as byte-similar copies, each drawing its own label
 * markup. One copy means one label rhythm: the name and query labels come from
 * the controls' own `FieldShell`, and the data-type chip group — which has no
 * control chrome of its own — is wrapped in the shell directly, exactly like
 * `BaseForm` does for `fileUploader`.
 */

export const COLUMN_DATA_TYPE_OPTIONS: { value: ColumnDataType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'currency', label: 'Currency' },
];

export interface ColumnFieldsProps {
  /** Namespaces the field ids so two of these can coexist on a page. */
  idPrefix: string;
  name: string;
  onNameChange: (name: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  dataType: ColumnDataType;
  onDataTypeChange: (dataType: ColumnDataType) => void;
  autoFocus?: boolean;
}

const ColumnFields: React.FC<ColumnFieldsProps> = ({
  idPrefix,
  name,
  onNameChange,
  query,
  onQueryChange,
  dataType,
  onDataTypeChange,
  autoFocus = false,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Input
        id={`${idPrefix}-name`}
        type="text"
        label={t('Column Name')}
        required
        value={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
        placeholder={t('e.g., Effective Date')}
        autoFocus={autoFocus}
      />

      <Textarea
        id={`${idPrefix}-query`}
        label={t('AI Query')}
        description={t('Defaults to the column name if left empty')}
        value={query}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onQueryChange(e.target.value)}
        placeholder={t('What is the effective date or execution date of this document?')}
        rows={3}
      />

      <FieldShell label={t('Data Type')} translate={false}>
        <div className="flex flex-wrap gap-2">
          {COLUMN_DATA_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onDataTypeChange(opt.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                dataType === opt.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
              }`}
            >
              {t(opt.label)}
            </button>
          ))}
        </div>
      </FieldShell>
    </>
  );
};

export default ColumnFields;
